import { useEffect, useRef, useState } from "preact/hooks";
import { hall } from "../hall-cache";
import { recordScreen } from "../perf";
import { useLive } from "../live";
import { crownedPortraitUrl, useOriginalAssets, useRecapUI } from "../features";
import { useKnownLook, type KnownLook } from "../appearance";
import { PeonLayers } from "../components/PeonLayers";
import { warmRecap, watchRecap } from "../components/Recap";
import { watchRecapLabel } from "../recap";
import { hallMonthRecap } from "../../shared/hall-memory.ts";
import type { HallMonth, RecapRef, Task } from "../../shared/types";
import type { Hairstyle, Shirt } from "../../shared/shirts.ts";
import { Empty, ErrorBox, ScreenTitle, Spinner } from "../components/Shell";
import { deviceTimezone } from "../store";
import { monthKeyIn } from "../format";

interface MonthWinner {
  key: string; // YYYY-MM
  label: string;
  name: string;
  count: number;
  current: boolean;
  /** The winner's shirt as the server sent it (HallMonth.shirt); undefined when it was not carried. */
  shirt?: Shirt | null;
  /** The winner's hairstyle as the server sent it (HallMonth.hair); undefined when it was not carried. */
  hair?: Hairstyle | null;
  /** The winner's beard and extras, by the same rules (null when never chosen, undefined when not carried). */
  beard?: string | null;
  accessories?: string[] | null;
  /** The winner's worn badges, by the same rules. */
  badges?: string[] | null;
  /** The month's season recap as the server sent it (HallMonth.recap); undefined when it was not carried. */
  recap?: RecapRef | null;
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Same rule as the Peon of the Month card, applied to every month on record:
 * most completions wins, ties go to the most recent completion. Grouped by
 * device-local month.
 *
 * Legacy: the screen below now fetches server/db.ts's hallOfFame (F4/T7),
 * which does the same grouping and tie-break in one SQL statement plus a
 * pick in JS, so the whole done list is never downloaded to the phone just
 * to build this list. This function is kept (and exported) in case anything
 * still tests it directly; it is otherwise unused.
 */
export function computeHall(done: Task[], now = new Date()): MonthWinner[] {
  const months = new Map<string, Map<string, { count: number; latest: number }>>();
  for (const t of done) {
    if (!t.done_at || !t.done_by) continue;
    const d = new Date(t.done_at);
    const key = monthKey(d);
    const peons = months.get(key) ?? new Map();
    const p = peons.get(t.done_by) ?? { count: 0, latest: 0 };
    p.count++;
    p.latest = Math.max(p.latest, d.getTime());
    peons.set(t.done_by, p);
    months.set(key, peons);
  }
  const currentKey = monthKey(now);
  return [...months.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([key, peons]) => {
      const [name, best] = [...peons.entries()].sort(([, a], [, b]) => b.count - a.count || b.latest - a.latest)[0];
      const [y, m] = key.split("-").map(Number);
      return {
        key,
        label: new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }),
        name,
        count: best.count,
        current: key === currentKey,
      };
    });
}

/**
 * Turns the server's HallMonth[] (key "YYYY-MM") into what the screen renders.
 * The request sends the device's zone (`tz`), so the server groups each
 * completion in its calendar month there, and "current" (the month read "so
 * far") is that zone's month too: on the evening of Sep 30 in Toronto it is
 * still September. The label is built from the key alone, with an explicit
 * UTC date formatted in UTC, so no offset can shift it into another month.
 */
function withLabels(months: HallMonth[], tz: string): MonthWinner[] {
  const currentKey = monthKeyIn(tz);
  return months.map((m) => {
    const [y, mo] = m.key.split("-").map(Number);
    const label = new Date(Date.UTC(y, mo - 1, 1)).toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
    // The beard and extras ride beside the hair (docs/peon-look.md "API"); read loosely, for a server that predates them.
    const worn = m as HallMonth & KnownLook;
    return { key: m.key, label, name: m.name, count: m.count, current: m.key === currentKey, shirt: m.shirt, hair: m.hair, beard: worn.beard, accessories: worn.accessories, badges: worn.badges, recap: m.recap };
  });
}

/** The month's crowned portrait, in its winner's shirt (owner, 2026-09-24), hair, beard and extras (2026-10-04). */
function HallPortrait({ name, known, originalAssets }: { name: string; known: KnownLook; originalAssets: boolean }) {
  const worn = useKnownLook(name, known);
  return (
    <span class="peon-stack">
      <img class="hall-portrait" src={crownedPortraitUrl(originalAssets, worn.shirt)} alt="" />
      {originalAssets && <PeonLayers look={worn} crowned />}
    </span>
  );
}

/**
 * A month's season recap (owner, 2026-11-02: "you should still be able to watch previous month
 * recaps"): the crown card's replay row (src/views/Board.tsx RecapRow), its title and the quiet
 * "Watch recap", across the foot of the month's card under the standings' rule. The card is a list
 * item, not a link, so the control sits in it. The player's signed links are asked for once the row
 * is on screen, not for every month on record.
 */
function HallRecap({ recap }: { recap: RecapRef }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      warmRecap(recap);
      return;
    }
    const seen = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      seen.disconnect();
      warmRecap(recap);
    });
    seen.observe(el);
    return () => seen.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recap.id]);
  return (
    <div class="hall-recap" ref={ref}>
      <span class="potm-recap-title" aria-hidden="true">{recap.title}</span>
      <button type="button" class="btn-quiet potm-recap-watch" aria-label={watchRecapLabel(recap)} onClick={() => watchRecap(recap)}>
        Watch recap
      </button>
    </div>
  );
}

/**
 * Reports how long the Hall took to show (shared/perf.ts "perf.screen"), once per mount: from the
 * press that opened it (the crown card's pointer-down mark, hall.takePressedAt) to the mount, from
 * the mount to the first rows, and the wait for the request. `fromMemory`: the rows were there at mount.
 */
function useScreenTiming(fromMemory: boolean): { fetched: (ms: number) => void; shown: () => void } {
  const ref = useRef<{ mountedAt: number; pressedAt: number | null; fromMemory: boolean; fetchMs: number | null; reported: boolean } | null>(null);
  ref.current ??= { mountedAt: performance.now(), pressedAt: hall.takePressedAt(), fromMemory, fetchMs: null, reported: false };
  return useRef({
    fetched(ms: number) {
      const t = ref.current!;
      if (t.fetchMs === null) t.fetchMs = ms;
    },
    shown() {
      const t = ref.current!;
      if (t.reported) return;
      t.reported = true;
      recordScreen(t.fromMemory ? "hall-remembered" : "hall", performance.now() - t.mountedAt, {
        press: t.pressedAt === null ? null : t.mountedAt - t.pressedAt,
        fetch: t.fetchMs,
      });
    },
  }).current;
}

export function HallOfFame() {
  // The months shown last time (this launch or an earlier one, kept on the device) paint at once
  // (shared/hall-memory.ts); the request replaces them.
  const [rows, setRows] = useState<MonthWinner[] | null>(() => {
    const remembered = hall.recall();
    return remembered ? withLabels(remembered, deviceTimezone()) : null;
  });
  const [error, setError] = useState<string | null>(null);
  const originalAssets = useOriginalAssets();
  // Each month's recap shows under the `recapUI` flag, the household's switch or this device's.
  const recapUI = useRecapUI();
  const timing = useScreenTiming(rows !== null);

  const reload = useLive(async (initial) => {
    try {
      const tz = deviceTimezone();
      const started = performance.now();
      const months = await hall.fetch(tz);
      timing.fetched(performance.now() - started);
      hall.remember(months);
      setRows(withLabels(months, tz));
      setError(null);
    } catch (e) {
      if (initial) setError(e instanceof Error ? e.message : "Could not load");
    }
  }, []);
  useEffect(() => {
    if (rows !== null) timing.shown();
  }, [rows]);

  return (
    <>
      <main class="content content-wide">
        <ScreenTitle title="Peon Hall of Fame" />
        {error && <ErrorBox message={error} onRetry={reload} />}
        {!error && rows === null && <Spinner />}
        {rows && rows.length === 0 && <Empty>No peon has been crowned yet. Complete a task to start the record.</Empty>}
        {rows && rows.length > 0 && (
          <ol class="hall">
            {rows.map((r) => {
              const recap = hallMonthRecap(recapUI, r);
              return (
                <li key={r.key} class={`card hall-row ${r.current ? "current" : ""}${recap ? " has-recap" : ""}`}>
                  <HallPortrait name={r.name} known={r} originalAssets={originalAssets} />
                  <div class="hall-body">
                    <div class="hall-month">
                      {r.label}
                      {r.current ? " · so far" : ""}
                    </div>
                    <div class="hall-name">{r.name}</div>
                    <div class="hall-count">
                      {r.count} task{r.count === 1 ? "" : "s"} completed
                    </div>
                  </div>
                  {recap && <HallRecap recap={recap} />}
                </li>
              );
            })}
          </ol>
        )}
      </main>
    </>
  );
}
